import { auth, profile } from '@/api'
import {
	AUTH_GOOGLE_PROFILE_GET_ERROR,
	AUTH_LOG_IN_ERRORS,
	AUTH_PROFILE_GET,
	AUTH_PROFILE_GET_ERROR,
	AUTH_PROFILE_UPDATE,
	AUTH_PROFILE_UPDATE_ERROR,
	AUTH_LOADING_STATUS,
} from './types'

import { APP_SHOW_ALERT, APP_HIDE_ALERT } from '../app/types'
import firebase from 'firebase/compat'
import TokenService from '@/services/token.service'

const login = async (ctx, credentials) => {
	try {
		ctx.commit(AUTH_LOADING_STATUS, true)
		const {
			data: { token, device_id },
		} = await auth.login(credentials)

		TokenService.saveAccessToken(token)
		TokenService.saveDeviceId(device_id)
		TokenService.saveEmail(credentials.login)

		await ctx.dispatch('getProfile')

		ctx.dispatch(
			'settings/sendDwhAction',
			{
				credentials: JSON.parse(localStorage.getItem('settings')).dwh.value,
				payload: {
					category: 'App Start',
					action: 'login_finish',
					module: 'App Launch',
					session_id: ctx.rootGetters['app/sessionId'],
					version: process.env.VUE_APP_VERSION,
					platform: 'web app',
					login: credentials.login,
					data: 'Success',
				},
			},
			{ root: true }
		)

		return true
	} catch (e) {
		console.error(e)

		const errorMessage = e.response.data[0]?.message || e.response.data.message
		ctx.commit(AUTH_LOG_IN_ERRORS, errorMessage)
		console.log('error')
		ctx.dispatch(
			'settings/sendDwhAction',
			{
				credentials: JSON.parse(localStorage.getItem('settings')).dwh.value,
				payload: {
					category: 'App Start',
					action: 'login_finish',
					module: 'App Launch',
					session_id: ctx.rootGetters['app/sessionId'],
					version: process.env.VUE_APP_VERSION,
					platform: 'web app',
					login: credentials.login,
					data: 'Error',
				},
			},
			{ root: true }
		)

		throw e
	} finally {
		ctx.commit(AUTH_LOADING_STATUS, false)
	}
}

const signUp = async (ctx, payload) => {
	try {
		ctx.commit(AUTH_LOADING_STATUS, true)
		await auth.register(payload)
	} catch (e) {
		console.log(e)
		if (e.response.status === 423) {
			const closeTimeout = setTimeout(() => {
				ctx.commit(`app/${APP_HIDE_ALERT}`, {}, { root: true })
			}, 5000)
			ctx.commit(
				`app/${APP_SHOW_ALERT}`,
				{
					message: e.response.data[0]?.message,
					closeTimeout,
					type: 'success',
				},
				{ root: true }
			)
			return true
		} else {
			const closeTimeout = setTimeout(() => {
				ctx.commit(`app/${APP_HIDE_ALERT}`, {}, { root: true })
			}, 5000)
			ctx.commit(
				`app/${APP_SHOW_ALERT}`,
				{
					message: e.response.data[0]?.message,
					closeTimeout,
					type: 'error',
				},
				{ root: true }
			)
			return false
		}
	} finally {
		ctx.commit(AUTH_LOADING_STATUS, false)
	}
}

const logout = async () => {
	try {
		await auth.logout()
		TokenService.clearStorageTokens()
		TokenService.removeDeviceId()
		localStorage.removeItem('settings')
		window.location.reload()
		return true
	} catch (e) {
		console.error(e)
		throw e
	}
}

const getProfile = async (ctx) => {
	try {
		ctx.commit(AUTH_LOADING_STATUS, true)
		const { data } = await profile.getProfile()

		if (!data) {
			throw 'Unauthorized'
		}

		ctx.commit(AUTH_PROFILE_GET, data)
	} catch (e) {
		ctx.commit(AUTH_PROFILE_GET_ERROR)
		console.log(e)
	} finally {
		ctx.commit(AUTH_LOADING_STATUS, false)
	}
}

const updateProfile = async (ctx, profileData) => {
	try {
		const { data } = await profile.updateProfile(profileData)
		ctx.commit(AUTH_PROFILE_UPDATE, data)
	} catch (e) {
		ctx.commit(AUTH_PROFILE_UPDATE_ERROR)
		console.log(e)
	}
}

const signInWithApple = async (ctx) => {
	const provider = new firebase.auth.OAuthProvider('apple.com')

	try {
		const { user } = await firebase.auth().signInWithPopup(provider)
		const accessToken = user.multiFactor.user.accessToken
		const payload = {
			token: accessToken,
			user: {
				email: user.multiFactor.user.email,
				name: user.multiFactor.user.displayName,
				photoUrl:
					'https://www.pinpng.com/pngs/m/341-3415688_no-avatar-png-transparent-png.png',
			},
		}
		ctx.dispatch('sendAuthDataToAPI', payload)
	} catch (e) {
		ctx.commit(AUTH_GOOGLE_PROFILE_GET_ERROR, e)
		console.error(e)
		throw e
	}
}

const signInWithFacebook = async (ctx) => {
	const provider = new firebase.auth.FacebookAuthProvider()
	try {
		const { user } = await firebase.auth().signInWithPopup(provider)
		const accessToken = user.multiFactor.user.accessToken
		const payload = {
			token: accessToken,
			user: {
				email: user.multiFactor.user.email,
				name: user.multiFactor.user.displayName,
				photoUrl: user.multiFactor.user.photoURL,
			},
		}
		if (!payload.user.email) {
			throw 'Please add e-mail address to your facebook account or choose another authorization provider'
		}
		ctx.dispatch('sendAuthDataToAPI', payload)
	} catch (e) {
		if (e.message.endsWith('(auth/account-exists-with-different-credential).')) {
			ctx.commit(
				AUTH_GOOGLE_PROFILE_GET_ERROR,
				'An account already exists with the same email address but different sign-in credentials'
			)
		} else {
			ctx.commit(AUTH_GOOGLE_PROFILE_GET_ERROR, e)
		}
		console.error(e)
		throw e
	}
}

const signInWithGoogle = async (ctx) => {
	const provider = new firebase.auth.GoogleAuthProvider()

	try {
		const { user } = await firebase.auth().signInWithPopup(provider)

		const token = await user.getIdToken()
		const payload = {
			token,
			user: {
				email: user.email,
				name: user.displayName,
				photoUrl: user.photoURL,
			},
		}
		ctx.dispatch('sendAuthDataToAPI', payload)
	} catch (e) {
		ctx.commit(AUTH_GOOGLE_PROFILE_GET_ERROR, e)
		console.error(e)
		throw e
	}
}

const sendAuthDataToAPI = async (ctx, payload) => {
	try {
		await auth.socialLogin(payload)
		ctx.dispatch('getProfile')

		return true
	} catch (e) {
		ctx.commit(AUTH_GOOGLE_PROFILE_GET_ERROR, e.response.data.message)
		console.error(e)
		throw e
	}
}

const restoreUserPassword = async (ctx, payload) => {
	try {
		ctx.commit(AUTH_LOADING_STATUS, true)
		const { data } = await auth.restorePassword(payload)
		return data
	} catch (e) {
		console.error(e)

		const errorMessage = e.response.data[0]?.message || e.response.data.message
		ctx.commit(AUTH_LOG_IN_ERRORS, errorMessage)
	} finally {
		ctx.commit(AUTH_LOADING_STATUS, false)
	}
}

export default {
	login,
	logout,
	getProfile,
	signInWithGoogle,
	sendAuthDataToAPI,
	updateProfile,
	signInWithFacebook,
	signInWithApple,
	restoreUserPassword,
	signUp,
}
